import { ID, Task, UpdateGoal, UpdateTask } from '@griply/types';
import { useCallback } from 'react';

export type UseDeleteTasksOptions = {
  allTasks: Task[];
  updateTasksAsync: (updates: UpdateTask[]) => Promise<unknown>;
  updateGoal?: (params: { goal: UpdateGoal }) => void;
  onTaskDeleted?: (task: Task) => void;
};

type GoalAdjustment = { taskCount: number; completedTaskCount: number };

export const useDeleteTasks = ({
  allTasks,
  updateTasksAsync,
  updateGoal,
  onTaskDeleted,
}: UseDeleteTasksOptions) =>
  useCallback(
    async (tasks: Task[], updateGoals?: boolean) => {
      if (!tasks.length) {
        return;
      }

      const goalAdjustments = updateGoals
        ? tasks.reduce<Record<ID, GoalAdjustment>>((acc, task) => {
            if (!task.goalId) {
              return acc;
            }
            const current = acc[task.goalId] ?? {
              taskCount: 0,
              completedTaskCount: 0,
            };
            current.taskCount -= 1;
            if (task.completedAt) {
              current.completedTaskCount -= 1;
            }
            acc[task.goalId] = current;
            return acc;
          }, {})
        : {};

      const deletedAt = new Date();
      const selectedIds = new Set<ID>(tasks.map(({ id }) => id));
      const updates: UpdateTask[] = [];
      const childUpdatesSeen = new Set<ID>();
      const parentPrunes = new Map<ID, Set<ID>>();

      tasks.forEach((task) => {
        updates.push({ id: task.id, deletedAt });
        onTaskDeleted?.(task);

        task.childIds?.forEach((childId) => {
          if (selectedIds.has(childId) || childUpdatesSeen.has(childId)) {
            return;
          }
          childUpdatesSeen.add(childId);
          updates.push({ id: childId, deletedAt });
          const childTask = allTasks.find(({ id }) => id === childId);
          if (childTask) {
            onTaskDeleted?.(childTask);
          }
        });

        const parentId = task.parentIds?.[task.parentIds.length - 1];
        if (!parentId || selectedIds.has(parentId)) {
          return;
        }
        const existing = parentPrunes.get(parentId) ?? new Set<ID>();
        existing.add(task.id);
        parentPrunes.set(parentId, existing);
      });

      parentPrunes.forEach((removedChildIds, parentId) => {
        const parent = allTasks.find(({ id }) => id === parentId);
        if (!parent?.childIds?.length) {
          return;
        }
        updates.push({
          id: parentId,
          childIds: parent.childIds.filter((id) => !removedChildIds.has(id)),
        });
      });

      if (updates.length) {
        await updateTasksAsync(updates);
      }

      if (!updateGoal) {
        return;
      }
      Object.entries(goalAdjustments).forEach(
        ([goalId, { taskCount, completedTaskCount }]) => {
          updateGoal({
            goal: { id: goalId, taskCount, completedTaskCount },
          });
        },
      );
    },
    [allTasks, updateTasksAsync, updateGoal, onTaskDeleted],
  );
